/**
 * Förberedelseplaner – text och visningsrader för gränssnittet. Rena funktioner (test/prep-ui.test.ts):
 * underlaget kommer från core (proposePlan, reviewPlan, computeAutoDrafts), här blir det bara text på
 * valt språk. Användarens egna texter (mallnamn, stegtitlar, källans rubriker) visas alltid som de är och
 * översätts aldrig. Färg är aldrig enda signal: varje tillstånd har text och en ikonnyckel.
 */
import {
  buildPlanCommit,
  planEventIndex,
  planForSource,
  PREP_MAX_DAYS_BEFORE,
  PREP_MAX_DURATION,
  PREP_MAX_STEPS,
  PREP_MIN_DAYS_BEFORE,
  PREP_MIN_DURATION,
  prepRuleIdFor,
  proposePlan,
  starterPrepTemplate,
  Temporal,
  timingInstants,
  validatePrepTemplateContent,
  type AppState,
  type AutoDraft,
  type AutoDraftSkip,
  type CalendarDef,
  type DeadlineEntry,
  type DraftSkipReason,
  type ISODate,
  type ISOInstant,
  type PlanProposal,
  type PlanReview,
  type PlanSessionProposal,
  type PlanSignal,
  type PlanStepStatus,
  type PlanUnsupportedReason,
  type PrepLink,
  type PrepPlan,
  type PrepPlanBasis,
  type PrepRule,
  type PrepStepDef,
  type PrepTemplate,
  type PrepTemplateContent,
  type TemplateIssue,
  type Timing,
} from '@autoday/core';
import { plural, tr } from '../../i18n/i18n.ts';
import { formatClock, formatDayMonth, formatDaysAgo, formatLocalClock, formatMinutes, formatWeekdayDate, weekdayName } from '../../i18n/format.ts';

export type PrepTone = 'accent' | 'ok' | 'info' | 'warn' | 'risk' | 'muted';
export type PrepIcon = 'planned' | 'done' | 'manual' | 'removed' | 'syncing' | 'past' | 'create' | 'move' | 'keep' | 'impossible';
export interface PrepStatus {
  text: string;
  tone: PrepTone;
  icon: PrepIcon;
}

// ---------------------------------------------------------------------------
// Tider och dagar
// ---------------------------------------------------------------------------

const hhmm = (s: string) => formatClock(Number(s.slice(0, 2)), Number(s.slice(3, 5)));
const sentence = (s: string) => (/[.!?]$/.test(s) ? s : s + '.');
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Lokal väggtid i en annan zon som väggtid i visningszonen (samma ögonblick). Oftast samma zon. */
export function toDisplayLocal(local: string, zone: string | undefined, tz: string): string {
  if (!zone || zone === tz) return local;
  return Temporal.PlainDateTime.from(local).toZonedDateTime(zone, { disambiguation: 'compatible' }).withTimeZone(tz).toPlainDateTime().toString({ smallestUnit: 'minute' });
}

/** "tis 16 juni 08.00–08.45" (long: "tisdag 16 juni …"). Slutar passet en annan dag står den dagen med. */
export function sessionWhen(start: string, end: string, long = false): string {
  const day = formatWeekdayDate(start.slice(0, 10), long);
  if (end.slice(0, 10) !== start.slice(0, 10)) return `${day} ${formatLocalClock(start)} – ${formatWeekdayDate(end.slice(0, 10), long)} ${formatLocalClock(end)}`;
  return `${day} ${formatLocalClock(start)}–${formatLocalClock(end)}`;
}

/** Ett kalenderpass tid i visningszonen (även om passet ändrats till heldag eller en annan zon). */
export function timingSpan(timing: Timing, tz: string): { start: string; end: string; allDay: boolean; minutes: number } {
  const { start, end } = timingInstants(timing, tz);
  const local = (i: Temporal.Instant) => i.toZonedDateTimeISO(tz).toPlainDateTime().toString({ smallestUnit: 'minute' });
  return { start: local(start), end: local(end), allDay: timing.kind === 'allDay', minutes: Math.round((end.epochMilliseconds - start.epochMilliseconds) / 60_000) };
}

/** Avvikelse från önskad dag (kalenderdagar). */
export function deviationText(days: number | undefined): string | null {
  if (days === undefined) return null;
  if (days === 0) return tr('på önskad dag', 'on the preferred day');
  const n = Math.abs(days);
  if (days < 0) return n === 1 ? tr('1 dag tidigare än önskat', '1 day earlier than preferred') : tr(`${n} dagar tidigare än önskat`, `${n} days earlier than preferred`);
  return n === 1 ? tr('1 dag senare än önskat', '1 day later than preferred') : tr(`${n} dagar senare än önskat`, `${n} days later than preferred`);
}

const sameDays = (a: readonly number[], b: readonly number[]) => a.length === b.length && a.every((x, i) => x === b[i]);
const normDays = (d: readonly number[]) => [...new Set(d)].sort((x, y) => x - y);
const WEEKDAYS = [1, 2, 3, 4, 5];
const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];
const WEEKEND = [6, 7];

/** Veckodagarna som substantiv: "vardagar", "alla dagar", "helger" eller "mån, ons, fre". */
export function weekdaysText(days: readonly number[]): string {
  const d = normDays(days);
  if (sameDays(d, WEEKDAYS)) return tr('vardagar', 'weekdays');
  if (sameDays(d, ALL_DAYS)) return tr('alla dagar', 'every day');
  if (sameDays(d, WEEKEND)) return tr('helger', 'weekends');
  return d.map((x) => weekdayName(x, true)).join(', ');
}

/** "inom 08.00–17.00 på vardagar" / "within 08:00–17:00 on weekdays". */
export function windowPhrase(c: Pick<PrepTemplateContent, 'window' | 'weekdays'>): string {
  const range = `${hhmm(c.window.start)}–${hhmm(c.window.end)}`;
  const d = normDays(c.weekdays);
  const days = sameDays(d, ALL_DAYS) ? tr('alla dagar', 'every day') : sameDays(d, WEEKEND) ? tr('på helger', 'at weekends') : `${tr('på', 'on')} ${weekdaysText(d)}`;
  return tr(`inom ${range} ${days}`, `within ${range} ${days}`);
}

/** "3 vardagar före" / "1 day before". */
export function stepDaysText(n: number, dayCount: PrepTemplateContent['dayCount']): string {
  if (dayCount === 'weekdays') return n === 1 ? tr('1 vardag före', '1 weekday before') : tr(`${n} vardagar före`, `${n} weekdays before`);
  return n === 1 ? tr('1 dag före', '1 day before') : tr(`${n} dagar före`, `${n} days before`);
}

/** Kort sammanfattning av en mall: "2 steg · 1 h 30 min · 08.00–17.00, vardagar". */
export function templateSummary(c: PrepTemplateContent): string {
  const total = c.steps.reduce((n, s) => n + s.durationMinutes, 0);
  return `${plural(c.steps.length, ['steg', 'steg'], ['step', 'steps'])} · ${formatMinutes(total)} · ${hhmm(c.window.start)}–${hhmm(c.window.end)}, ${weekdaysText(c.weekdays)}`;
}

/** Hur gammalt importunderlaget är: "importerad för 2 dagar sedan". */
export function importedText(at: ISOInstant | null, today: ISODate, tz: string): string | null {
  if (!at) return null;
  const d = Temporal.Instant.from(at).toZonedDateTimeISO(tz).toPlainDate().toString();
  return `${tr('importerad', 'imported')} ${formatDaysAgo(d, today)}`;
}

/** Deadlinen sist i tidslinjen: "fre 19 juni" (tidsatt: "fre 19 juni 10.00"). */
export function deadlineWhen(basis: Pick<PrepPlanBasis, 'deadlineDate' | 'deadlineStart' | 'allDay'>, tz: string, long = false): string {
  const day = formatWeekdayDate(basis.deadlineDate, long);
  if (basis.allDay) return day;
  const z = Temporal.Instant.from(basis.deadlineStart).toZonedDateTimeISO(tz);
  return `${day} ${formatClock(z.hour, z.minute)}`;
}

// ---------------------------------------------------------------------------
// Tillstånd, problem och skäl
// ---------------------------------------------------------------------------

/** Ett godkänt pass tillstånd (reviewPlan). "Synkar" betyder att passet inte finns lokalt – aldrig "komplett". */
export function stepStatus(state: PlanStepStatus['state']): PrepStatus {
  switch (state) {
    case 'planned':
      return { text: tr('Planerad', 'Planned'), tone: 'accent', icon: 'planned' };
    case 'done':
      return { text: tr('Klar', 'Done'), tone: 'ok', icon: 'done' };
    case 'manual':
      return { text: tr('Ditt eget val', 'Your own choice'), tone: 'info', icon: 'manual' };
    case 'removed':
      return { text: tr('Borttaget', 'Removed'), tone: 'muted', icon: 'removed' };
    case 'syncing':
      return { text: tr('Synkar', 'Syncing'), tone: 'warn', icon: 'syncing' };
    case 'past':
      return { text: tr('Passerat', 'Passed'), tone: 'muted', icon: 'past' };
  }
}

/**
 * Vad ett förslag gör med steget. Kan förslaget inte godkännas (status ≠ 'ok') läggs ingenting in – ett steg
 * som skulle ha rymts sägs då bara rymmas, aldrig "läggs in" (inget dolt delresultat).
 */
export function sessionStatus(s: Pick<PlanSessionProposal, 'action'>, kind: PlanProposal['kind'], status: PlanProposal['status'] = 'ok'): PrepStatus {
  if (status !== 'ok' && (s.action === 'create' || s.action === 'move')) return { text: tr('Skulle rymmas här', 'Would fit here'), tone: 'muted', icon: 'planned' };
  switch (s.action) {
    case 'create':
      return kind === 'replan' ? { text: tr('Läggs in igen', 'Added again'), tone: 'accent', icon: 'create' } : { text: tr('Läggs in', 'Will be added'), tone: 'accent', icon: 'create' };
    case 'move':
      return { text: tr('Flyttas', 'Moves'), tone: 'accent', icon: 'move' };
    case 'keep':
      return { text: tr('Ligger kvar', 'Stays'), tone: 'muted', icon: 'keep' };
    case 'done':
      return { text: tr('Klart', 'Done'), tone: 'ok', icon: 'done' };
    case 'manual':
      return { text: tr('Ditt eget val', 'Your own choice'), tone: 'info', icon: 'manual' };
    case 'removed':
      return { text: tr('Borttaget', 'Removed'), tone: 'muted', icon: 'removed' };
    case 'past':
      return { text: tr('Passerat', 'Passed'), tone: 'muted', icon: 'past' };
    case 'impossible':
      return { text: tr('Ryms inte', 'Does not fit'), tone: 'risk', icon: 'impossible' };
  }
}

/** Varför ett befintligt pass behöver ändras (omplanering). */
export function problemText(p: NonNullable<PlanSessionProposal['problem']>): string {
  switch (p.kind) {
    case 'conflict':
      return tr(`krockar med ${p.with.join(', ')}`, `clashes with ${p.with.join(', ')}`);
    case 'after-deadline':
      return tr('ligger efter deadline', 'is after the deadline');
    case 'outside-window':
      return tr('ligger utanför mallens tider', 'is outside the template’s times');
    case 'order':
      return tr('ligger före ett tidigare steg', 'is before an earlier step');
    case 'realign':
      return tr('deadlinen har ändrats', 'the deadline has changed');
  }
}

export function whyText(why: PlanSessionProposal['why']): string {
  return why === 'after-previous'
    ? tr('Ryms inte efter föregående steg före deadline.', 'Does not fit after the previous step before the deadline.')
    : tr('Ingen ledig tid före deadline inom mallens tider.', 'No free time before the deadline within the template’s times.');
}

/** Granskningssignal från reviewPlan i klartext. titleOf ger stegets titel (användarens text). */
export function signalText(s: PlanSignal, titleOf: (stepId: string) => string): string {
  switch (s.kind) {
    case 'deadline-moved':
      return tr(`Deadlinen har flyttats från ${formatWeekdayDate(s.from.date)} till ${formatWeekdayDate(s.to.date)}.`, `The deadline has moved from ${formatWeekdayDate(s.from.date)} to ${formatWeekdayDate(s.to.date)}.`);
    case 'zone-changed':
      return tr(`Tidszonen har ändrats från ${s.from} till ${s.to}.`, `The time zone has changed from ${s.from} to ${s.to}.`);
    case 'source-missing':
      return tr('Källposten saknades i senaste importen.', 'The source item was missing in the latest import.');
    case 'source-gone':
      return tr('Källposten finns inte längre eller är dold.', 'The source item no longer exists or is hidden.');
    case 'source-review':
      return tr('Källposten har ändrats och behöver granskas.', 'The source item has changed and needs review.');
    case 'conflict':
      return tr(`${titleOf(s.stepId)} krockar med ${s.with.join(', ')}.`, `${titleOf(s.stepId)} clashes with ${s.with.join(', ')}.`);
    case 'after-deadline':
      return tr(`${titleOf(s.stepId)} ligger efter deadline.`, `${titleOf(s.stepId)} is after the deadline.`);
  }
}

/** Kort form för listor (Viktigt): "deadlinen har flyttats, krock med Veckomöte". */
export function signalShort(s: PlanSignal): string {
  switch (s.kind) {
    case 'deadline-moved':
      return tr('deadlinen har flyttats', 'the deadline has moved');
    case 'zone-changed':
      return tr('tidszonen har ändrats', 'the time zone has changed');
    case 'source-missing':
      return tr('källposten saknades i senaste importen', 'the source item was missing in the latest import');
    case 'source-gone':
      return tr('källposten finns inte längre', 'the source item no longer exists');
    case 'source-review':
      return tr('källposten behöver granskas', 'the source item needs review');
    case 'conflict':
      return tr(`krock med ${s.with[0] ?? ''}`, `clash with ${s.with[0] ?? ''}`);
    case 'after-deadline':
      return tr('ett pass ligger efter deadline', 'a session is after the deadline');
  }
}

/** Varför ingen plan kan föreslås just nu. */
export function unsupportedText(reason: PlanUnsupportedReason | undefined): string {
  switch (reason) {
    case 'not-found':
      return tr('Posten finns inte längre.', 'The item no longer exists.');
    case 'archived':
      return tr('Posten är dold.', 'The item is hidden.');
    case 'missing':
      return tr('Posten saknades i senaste importen. Granska den först.', 'The item was missing in the latest import. Review it first.');
    case 'review':
      return tr('Källan har ändrats. Granska posten först.', 'The source has changed. Review the item first.');
    case 'not-deadline':
      return tr('Posten räknas inte som deadline.', 'The item does not count as a deadline.');
    case 'recurring':
      return tr('Återkommande källdatum stöds inte av förberedelseplaner ännu.', 'Recurring source dates are not supported by preparation plans yet.');
    case 'passed':
      return tr('Datumet har passerat.', 'The date has passed.');
    case 'done':
      return tr('Deadlinen är markerad klar här.', 'The deadline is marked done here.');
    case 'template-missing':
      return tr('Välj en mall.', 'Choose a template.');
    case 'template-invalid':
      return tr('Mallen behöver ändras innan den kan användas.', 'The template needs to be changed before it can be used.');
    case 'plan-active':
      return tr('Det finns redan en förberedelseplan för den här deadlinen.', 'There is already a preparation plan for this deadline.');
    case 'syncing':
      return tr('Alla pass i planen finns inte på den här enheten än. Vänta tills synken är klar.', 'Not all sessions in the plan are on this device yet. Wait until syncing has finished.');
    default:
      return tr('Ingen plan kan föreslås just nu.', 'No plan can be suggested right now.');
  }
}

/** Varför en kommande deadline från en regel inte fick något förslag. */
export function skipReasonText(reason: DraftSkipReason): string {
  switch (reason) {
    case 'passed':
      return tr('datumet har passerat', 'the date has passed');
    case 'done':
      return tr('markerad klar här', 'marked done here');
    case 'archived':
      return tr('posten är dold', 'the item is hidden');
    case 'missing':
      return tr('saknades i senaste importen', 'missing in the latest import');
    case 'review':
      return tr('behöver granskas', 'needs review');
    case 'not-deadline':
      return tr('räknas inte som deadline', 'does not count as a deadline');
    case 'recurring':
      return tr('återkommande datum stöds inte ännu', 'recurring dates are not supported yet');
    case 'has-plan':
      return tr('har redan en förberedelseplan', 'already has a preparation plan');
    case 'plan-ended':
      return tr('planen avslutades – planera igen i posten om du vill', 'the plan was ended – plan again in the item if you want');
    case 'dismissed':
      return tr('du valde "Inte nu" för det här datumet', 'you chose "Not now" for this date');
    case 'has-existing-prep':
      return tr('har redan en förberedelse', 'already has preparation');
    case 'template-missing':
      return tr('mallen finns inte längre', 'the template no longer exists');
  }
}

export function pausedReasonText(reason: PrepRule['pausedReason']): string {
  switch (reason) {
    case 'template-deleted':
      return tr('Pausad: mallen togs bort', 'Paused: the template was deleted');
    case 'restored':
      return tr('Pausad efter återställning – aktivera igen', 'Paused after a restore – turn it on again');
    case 'transferred':
      return tr('Pausad efter överföring', 'Paused after transfer');
    default:
      return tr('Pausad', 'Paused');
  }
}

/** Mallfel (validatePrepTemplateContent) på valt språk – koden avgör texten, inte core-meddelandet. */
export function templateIssueText(issue: TemplateIssue): string {
  const n = (issue.stepIndex ?? 0) + 1;
  switch (issue.code) {
    case 'name':
      return tr('Ge mallen ett namn (högst 80 tecken).', 'Give the template a name (at most 80 characters).');
    case 'steps-count':
      return tr(`En mall har 1–${PREP_MAX_STEPS} steg.`, `A template has 1–${PREP_MAX_STEPS} steps.`);
    case 'step-title':
      return tr(`Steg ${n} behöver en titel (högst 120 tecken).`, `Step ${n} needs a title (at most 120 characters).`);
    case 'step-duration':
      return tr(`Steg ${n}: längden ska vara ${PREP_MIN_DURATION}–${PREP_MAX_DURATION} minuter.`, `Step ${n}: the length must be ${PREP_MIN_DURATION}–${PREP_MAX_DURATION} minutes.`);
    case 'step-days':
      return tr(`Steg ${n}: dagar före deadline ska vara ${PREP_MIN_DAYS_BEFORE}–${PREP_MAX_DAYS_BEFORE}.`, `Step ${n}: days before the deadline must be ${PREP_MIN_DAYS_BEFORE}–${PREP_MAX_DAYS_BEFORE}.`);
    case 'step-order':
      return tr(`Steg ${n} kan inte ligga fler dagar före deadline än steget före. Stegen står i tidsordning.`, `Step ${n} cannot be more days before the deadline than the step before it. The steps are in time order.`);
    case 'step-ids':
      return tr('Stegen kunde inte identifieras. Stäng och öppna mallen igen.', 'The steps could not be identified. Close and open the template again.');
    case 'day-count':
      return tr('Välj om dagarna räknas som vardagar eller kalenderdagar.', 'Choose whether days count as weekdays or calendar days.');
    case 'weekdays':
      return tr('Välj minst en veckodag.', 'Choose at least one weekday.');
    case 'window':
      return tr('Tiden på dagen ska vara hela kvartar och sluta efter att den börjar (samma dag).', 'The time of day must be whole quarter hours and end after it starts (same day).');
    case 'window-short':
      return tr('Tiden på dagen är kortare än det längsta steget.', 'The time of day is shorter than the longest step.');
    case 'calendar':
      return tr('Välj en egen kalender (Privat eller Arbete). Importerade kalendrar är skrivskyddade.', 'Choose one of your own calendars (Personal or Work). Imported calendars are read-only.');
  }
}

// ---------------------------------------------------------------------------
// Förslag: knapp, anteckningar, förklaring, underlag
// ---------------------------------------------------------------------------

/** ETT entydigt primärval: "Lägg in 2 förberedelser" / "Godkänn ändringen – flytta 1 pass". */
export function approveLabel(p: Pick<PlanProposal, 'kind' | 'createCount' | 'moveCount'>): string {
  if (p.kind === 'approve') return p.createCount === 1 ? tr('Lägg in 1 förberedelse', 'Add 1 preparation session') : tr(`Lägg in ${p.createCount} förberedelser`, `Add ${p.createCount} preparation sessions`);
  const parts: string[] = [];
  if (p.moveCount) parts.push(tr(`flytta ${p.moveCount} pass`, p.moveCount === 1 ? 'move 1 session' : `move ${p.moveCount} sessions`));
  if (p.createCount) parts.push(tr(`lägg in ${p.createCount} igen`, p.createCount === 1 ? 'add 1 again' : `add ${p.createCount} again`));
  return parts.length ? `${tr('Godkänn ändringen', 'Approve the change')} – ${parts.join(tr(' och ', ' and '))}` : tr('Godkänn ändringen', 'Approve the change');
}

/** Besked efter ett lyckat godkännande. Planen tas bort via "Avsluta planen" – inget "Ångra" här. */
export function approvedText(p: Pick<PlanProposal, 'kind' | 'createCount' | 'moveCount'>, duplicate: boolean): string {
  if (duplicate) return tr('Det här var redan godkänt – inget lades in två gånger.', 'This was already approved – nothing was added twice.');
  if (p.kind === 'approve') return p.createCount === 1 ? tr('1 förberedelse inlagd.', '1 preparation session added.') : tr(`${p.createCount} förberedelser inlagda.`, `${p.createCount} preparation sessions added.`);
  const parts: string[] = [];
  if (p.moveCount) parts.push(p.moveCount === 1 ? tr('1 pass flyttat', '1 session moved') : tr(`${p.moveCount} pass flyttade`, `${p.moveCount} sessions moved`));
  if (p.createCount) parts.push(p.createCount === 1 ? tr('1 pass inlagt igen', '1 session added again') : tr(`${p.createCount} pass inlagda igen`, `${p.createCount} sessions added again`));
  return `${tr('Planen är ändrad', 'The plan has been updated')}${parts.length ? ': ' + parts.join(', ') : ''}.`;
}

/**
 * Anteckning per steg: 'Steg 1 av 2 i "Förbered i två steg" inför Månadsrapport'. Skrivs på språket vid
 * godkännandet och blir sedan användarens egen text i passet.
 */
export function planNotes(p: Pick<PlanProposal, 'sessions' | 'snapshot' | 'sourceTitle'>): Record<string, string> {
  const name = p.snapshot?.name ?? '';
  const total = p.sessions.length;
  return Object.fromEntries(p.sessions.map((s) => [s.stepId, tr(`Steg ${s.stepIndex + 1} av ${total} i "${name}" inför ${p.sourceTitle}`, `Step ${s.stepIndex + 1} of ${total} in "${name}" for ${p.sourceTitle}`)]));
}

/** Varför planen är möjlig – eller vilka steg som saknar utrymme. Ingen säkerhetsprocent. */
export function feasibilityText(p: PlanProposal): string {
  const phrase = p.snapshot ? windowPhrase(p.snapshot) : '';
  switch (p.status) {
    case 'ok':
      return p.kind === 'replan'
        ? tr(`Ändringen håller alla steg i ordning före deadline, ${phrase}.`, `The change keeps all steps in order before the deadline, ${phrase}.`)
        : tr(`Alla steg ryms i ordning före deadline, ${phrase}.`, `All steps fit in order before the deadline, ${phrase}.`);
    case 'impossible': {
      const names = p.sessions.filter((s) => s.action === 'impossible').map((s) => s.title).join(', ');
      return tr(`Alla steg ryms inte före deadline ${phrase}. Saknar utrymme: ${names}.`, `Not all steps fit before the deadline ${phrase}. No room for: ${names}.`);
    }
    case 'unchanged':
      return tr('Inget behöver flyttas – alla kommande pass ligger bra till.', 'Nothing needs to move – all upcoming sessions are fine where they are.');
    case 'unsupported':
      return unsupportedText(p.reason);
  }
}

/** Underlagets gränser i klartext. mode: gäst = den här enheten, konto = kontot. */
export function basisNotes(p: Pick<PlanProposal, 'uncertain' | 'hiddenCalendarsCounted' | 'calendarHidden' | 'searchedFrom' | 'searchedTo'>, mode: 'guest' | 'account'): string[] {
  const out = [
    mode === 'account'
      ? tr('Förslaget bygger på kalenderdata i ditt AutoDay-konto: egna aktiviteter (även i dolda kalendrar) och importerade poster som inte är markerade som lediga.', 'The suggestion is based on calendar data in your AutoDay account: your own activities (including hidden calendars) and imported items not marked as free.')
      : tr('Förslaget bygger på kalenderdata i AutoDay på den här enheten: egna aktiviteter (även i dolda kalendrar) och importerade poster som inte är markerade som lediga.', 'The suggestion is based on calendar data in AutoDay on this device: your own activities (including hidden calendars) and imported items not marked as free.'),
    tr('En importerad fil är en ögonblicksbild och kan vara äldre än originalkalendern.', 'An imported file is a snapshot and may be older than the original calendar.'),
  ];
  if (p.searchedFrom && p.searchedTo) out.push(tr(`Sökt ${formatDayMonth(p.searchedFrom)}–${formatDayMonth(p.searchedTo)}.`, `Searched ${formatDayMonth(p.searchedFrom)}–${formatDayMonth(p.searchedTo)}.`));
  const u = p.uncertain.length;
  if (u) out.push(u === 1 ? tr('1 återkommande post kunde inte tolkas helt och räknas som upptagen varje dag.', '1 recurring item could not be fully read and counts as busy every day.') : tr(`${u} återkommande poster kunde inte tolkas helt och räknas som upptagna varje dag.`, `${u} recurring items could not be fully read and count as busy every day.`));
  const h = p.hiddenCalendarsCounted;
  if (h) out.push(h === 1 ? tr('1 post i en dold kalender räknas som upptagen tid.', '1 item in a hidden calendar counts as busy time.') : tr(`${h} poster i dolda kalendrar räknas som upptagen tid.`, `${h} items in hidden calendars count as busy time.`));
  if (p.calendarHidden) out.push(tr('Kalendern som passen läggs i är dold – passen syns inte i kalendervyn förrän du visar den.', 'The calendar the sessions go into is hidden – they do not show in the calendar view until you show it.'));
  return out;
}

/** Kort sammanfattning av ett utkasts tider: "tis 16 juni 08.00 · tors 18 juni 08.00". */
export function draftTimesSummary(p: PlanProposal): string {
  if (p.status === 'impossible') return tr('Ryms inte – granska', 'Does not fit – review');
  if (p.status !== 'ok') return tr('Behöver granskas', 'Needs review');
  return p.sessions
    .filter((s) => s.start)
    .map((s) => `${formatWeekdayDate(s.start!.slice(0, 10))} ${formatLocalClock(s.start!)}`)
    .join(' · ');
}

/**
 * Signatur för ett förslag: ändras den har förslaget ändrats (nya tider, ny revision, nytt underlag).
 * Ett nytt förslag får ett nytt operations-ID – det gamla förslagets knapp godkänner aldrig nya tider.
 */
export function proposalSignature(p: PlanProposal): string {
  return JSON.stringify([
    p.kind,
    p.status,
    p.planId,
    p.templateId,
    p.templateRevision,
    p.expectedPlanRevision,
    p.basis?.deadlineStart ?? null,
    p.basis?.sourceHash ?? null,
    p.basis?.timeZone ?? null,
    p.sessions.map((s) => [s.stepId, s.action, s.start ?? null, s.end ?? null, s.expectedEventRevision ?? null]),
  ]);
}

// ---------------------------------------------------------------------------
// Tidslinjens rader
// ---------------------------------------------------------------------------

export interface TimelineRow {
  key: string;
  stepId: string;
  /** 1-baserat stegnummer och antal steg. */
  n: number;
  total: number;
  /** Användarens egen text. */
  title: string;
  when: string | null;
  /** Omplanering: tiden före ändringen. */
  from: string | null;
  duration: string;
  status: PrepStatus;
  note: string | null;
  noteTone: PrepTone;
  /** Hela meningen för skärmläsare. */
  sentence: string;
  eventId?: string;
  muted: boolean;
}

function rowSentence(r: { n: number; total: number; title: string; whenLong: string | null; fromLong: string | null; duration: string; status: string; note: string | null }): string {
  const head = `${tr('Steg', 'Step')} ${r.n} ${tr('av', 'of')} ${r.total}: ${r.title}${r.whenLong ? `, ${r.whenLong}` : ''}, ${r.duration}`;
  const parts = [head, r.status];
  if (r.fromLong) parts.push(tr(`Flyttas från ${r.fromLong}`, `Moves from ${r.fromLong}`));
  if (r.note) parts.push(r.note);
  return parts.map(sentence).join(' ');
}

/** Rader för en godkänd plan (statuskortet). review.steps följer planens steg i ordning. */
export function planTimelineRows(plan: PrepPlan, review: PlanReview, tz: string): TimelineRow[] {
  const total = review.steps.length;
  return review.steps.map((st, i) => {
    const step = plan.steps[i];
    let when: string | null = null;
    let whenLong: string | null = null;
    let minutes = step?.durationMinutes ?? 0;
    if (st.event) {
      const span = timingSpan(st.event.timing, tz);
      minutes = span.minutes;
      when = span.allDay ? `${formatWeekdayDate(span.start.slice(0, 10))} · ${tr('heldag', 'all day')}` : sessionWhen(span.start, span.end);
      whenLong = span.allDay ? `${formatWeekdayDate(span.start.slice(0, 10), true)}, ${tr('heldag', 'all day')}` : sessionWhen(span.start, span.end, true);
    } else if (step) {
      // Borttaget eller ännu inte synkat: visa tiden som planen lade in.
      const s = toDisplayLocal(step.approved.start, step.approved.timeZone, tz);
      const e = toDisplayLocal(step.approved.end, step.approved.timeZone, tz);
      when = sessionWhen(s, e);
      whenLong = sessionWhen(s, e, true);
    }
    const status = stepStatus(st.state);
    const duration = formatMinutes(minutes);
    const note = st.state === 'syncing' ? tr('Passet finns inte på den här enheten än.', 'The session is not on this device yet.') : st.state === 'manual' ? tr('Du har ändrat passet själv. Planen flyttar det inte utan att du väljer det.', 'You changed the session yourself. The plan does not move it unless you choose to.') : null;
    return {
      key: st.eventId,
      stepId: st.stepId,
      n: i + 1,
      total,
      title: st.title,
      when,
      from: null,
      duration,
      status,
      note,
      noteTone: st.state === 'syncing' ? 'warn' : 'muted',
      sentence: rowSentence({ n: i + 1, total, title: st.title, whenLong, fromLong: null, duration, status: status.text, note }),
      ...(st.event ? { eventId: st.eventId } : {}),
      muted: st.state === 'removed' || st.state === 'past',
    };
  });
}

/** Rader för ett förslag (ny plan eller omplanering), i stegens tidsordning. */
export function proposalTimelineRows(p: PlanProposal, tz: string): TimelineRow[] {
  const total = p.sessions.length;
  return p.sessions.map((s) => {
    const start = s.start ? toDisplayLocal(s.start, s.timeZone, tz) : null;
    const end = s.end ? toDisplayLocal(s.end, s.timeZone, tz) : null;
    const from = s.action === 'move' && s.from ? { start: toDisplayLocal(s.from.start, s.from.timeZone, tz), end: toDisplayLocal(s.from.end, s.from.timeZone, tz) } : null;
    const status = sessionStatus(s, p.kind, p.status);
    let note: string | null = null;
    if (s.action === 'impossible') {
      // Omplanering: passet ligger kvar där det ligger tills användaren väljer något annat.
      const now = s.from ? sessionWhen(toDisplayLocal(s.from.start, s.from.timeZone, tz), toDisplayLocal(s.from.end, s.from.timeZone, tz)) : null;
      note = whyText(s.why) + (s.problem ? ` ${cap(problemText(s.problem))}.` : '') + (now ? ' ' + tr(`Ligger nu ${now}.`, `Currently ${now}.`) : '');
    }
    else {
      const parts: string[] = [];
      if (s.problem && s.action !== 'keep') parts.push(problemText(s.problem));
      const dev = s.action === 'create' || s.action === 'move' ? deviationText(s.deviationDays) : null;
      if (dev) parts.push(dev);
      note = parts.length ? cap(parts.join(' · ')) : null;
    }
    const duration = formatMinutes(s.durationMinutes);
    return {
      key: s.stepId,
      stepId: s.stepId,
      n: s.stepIndex + 1,
      total,
      title: s.title,
      when: start && end ? sessionWhen(start, end) : null,
      from: from ? sessionWhen(from.start, from.end) : null,
      duration,
      status,
      note,
      noteTone: s.action === 'impossible' ? 'risk' : s.problem && s.action !== 'keep' ? 'warn' : 'muted',
      sentence: rowSentence({ n: s.stepIndex + 1, total, title: s.title, whenLong: start && end ? sessionWhen(start, end, true) : null, fromLong: from ? sessionWhen(from.start, from.end, true) : null, duration, status: status.text, note }),
      ...(s.eventId ? { eventId: s.eventId } : {}),
      muted: s.action === 'removed' || s.action === 'past' || s.action === 'keep' || s.action === 'done',
    };
  });
}

// ---------------------------------------------------------------------------
// Rader i Viktigt och Idag
// ---------------------------------------------------------------------------

/** Nästa kommande pass i en godkänd plan: "tis 16 juni 08.00". */
export function nextSessionWhen(review: PlanReview, tz: string): string | null {
  const next = review.steps.find((s) => (s.state === 'planned' || s.state === 'manual') && s.event);
  if (!next?.event) return null;
  const span = timingSpan(next.event.timing, tz);
  return span.allDay ? formatWeekdayDate(span.start.slice(0, 10)) : `${formatWeekdayDate(span.start.slice(0, 10))} ${formatLocalClock(span.start)}`;
}

/**
 * "Förberedelseplan: 2 pass · nästa tis 16 juni 08.00" / "… · 1 av 2 klara". "Alla förberedelser klara" gäller
 * bara passen – deadlinen själv klarmarkeras aldrig av planen.
 */
export function planLine(review: PlanReview, tz: string): string {
  const head = tr('Förberedelseplan', 'Preparation plan');
  if (review.completeness === 'syncing') return `${head}: ${tr('alla pass har inte synkats till den här enheten än', 'not all sessions have synced to this device yet')}`;
  const n = review.activeCount;
  if (!n) return `${head}: ${tr('alla pass är borttagna', 'all sessions have been removed')}`;
  const parts = [n === 1 ? tr('1 pass', '1 session') : tr(`${n} pass`, `${n} sessions`)];
  if (review.allDone) parts.push(tr('alla förberedelser klara', 'all preparation done'));
  else if (review.doneCount) parts.push(tr(`${review.doneCount} av ${n} klara`, `${review.doneCount} of ${n} done`));
  const next = review.allDone ? null : nextSessionWhen(review, tz);
  if (next) parts.push(tr(`nästa ${next}`, `next ${next}`));
  return `${head}: ${parts.join(' · ')}`;
}

/** "Planen behöver ses över: deadlinen har flyttats". */
export function reviewLine(review: PlanReview): string {
  const reasons = [...new Set(review.signals.map(signalShort))];
  return `${tr('Planen behöver ses över', 'The plan needs a look')}: ${reasons.join(', ')}`;
}

// ---------------------------------------------------------------------------
// Mallredigering: val och standardvärden
// ---------------------------------------------------------------------------

export const WINDOW_PRESETS: ReadonlyArray<{ id: string; sv: string; en: string; start: string; end: string }> = [
  { id: 'workday', sv: 'Arbetsdag 08–17', en: 'Work day 08–17', start: '08:00', end: '17:00' },
  { id: 'morning', sv: 'Förmiddag 08–12', en: 'Morning 08–12', start: '08:00', end: '12:00' },
  { id: 'afternoon', sv: 'Eftermiddag 13–17', en: 'Afternoon 13–17', start: '13:00', end: '17:00' },
  { id: 'evening', sv: 'Kväll 17–21', en: 'Evening 17–21', start: '17:00', end: '21:00' },
];

export function windowPresetId(w: { start: string; end: string }): string {
  return WINDOW_PRESETS.find((p) => p.start === w.start && p.end === w.end)?.id ?? 'custom';
}

/** Längder i 15-minuterssteg; ett befintligt avvikande värde (t.ex. 50 min från en annan enhet) behålls som val. */
export function durationOptions(current?: number): number[] {
  const out: number[] = [];
  for (let m = PREP_MIN_DURATION; m <= PREP_MAX_DURATION; m += 15) out.push(m);
  if (current !== undefined && !out.includes(current)) out.push(current);
  return out.sort((a, b) => a - b);
}

/** Alla kvartar på ett dygn: "00:00" … "23:45". */
export function quarterTimes(): string[] {
  return Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`);
}

/** Egna, skrivbara kalendrar (aldrig importkalendrar) – målkalender för passen. */
export function ownCalendars(state: Pick<AppState, 'calendars'>): CalendarDef[] {
  return Object.values(state.calendars).filter((c) => c.kind !== 'import');
}

/** Startmallens kalender: Arbete om den finns, annars första egna kalendern. */
export function starterCalendarId(state: Pick<AppState, 'calendars'>): string {
  const own = ownCalendars(state);
  return own.find((c) => c.id === 'cal-arbete')?.id ?? own[0]?.id ?? 'cal-arbete';
}

/** Förvald mall: källans regel, annars mallen i den senast godkända planen, annars senast ändrade mallen. */
export function defaultTemplateId(state: AppState, sourceId: string): string | null {
  const templates = state.prepTemplates ?? {};
  const rule = state.prepRules?.[prepRuleIdFor(sourceId)];
  if (rule && templates[rule.templateId]) return rule.templateId;
  const recent = Object.values(state.prepPlans ?? {})
    .filter((p) => templates[p.templateId])
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
  if (recent) return recent.templateId;
  return Object.values(templates).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt) || a.id.localeCompare(b.id))[0]?.id ?? null;
}

/** Erbjud automatiska förslag efter FÖRSTA godkända planen för en källa som saknar regel. Aldrig förvalt. */
export function shouldOfferRule(state: AppState, sourceId: string): boolean {
  if (!state.sources[sourceId] || state.prepRules?.[prepRuleIdFor(sourceId)]) return false;
  return Object.values(state.prepPlans ?? {}).filter((p) => state.sourceItems[p.sourceKey]?.sourceId === sourceId).length === 1;
}

/** Mallarna i namnordning (samma ordning i alla väljare). */
export function sortedTemplates(state: Pick<AppState, 'prepTemplates'>): PrepTemplate[] {
  return Object.values(state.prepTemplates ?? {}).sort((a, b) => a.name.localeCompare(b.name, 'sv') || a.id.localeCompare(b.id));
}

/** Finns startmallen redan (på något av språken)? Då erbjuds den inte igen. */
export function hasStarterTemplate(state: Pick<AppState, 'prepTemplates'>): boolean {
  const names = new Set([starterPrepTemplate('sv').name, starterPrepTemplate('en').name]);
  return Object.values(state.prepTemplates ?? {}).some((t) => names.has(t.name.trim()));
}

// ---------------------------------------------------------------------------
// Planens tillstånd i vyerna
// ---------------------------------------------------------------------------

/**
 * Kan en NY plan föreslås för källposten? null = ja. Samma kontroller som core gör vid förslag och
 * godkännande (proposePlan utan mall stannar vid första skälet; "mallen saknas" betyder att posten duger).
 */
export function newPlanBlock(state: AppState, sourceKey: string, now: Temporal.Instant): PlanUnsupportedReason | null {
  if (planForSource(state, sourceKey)?.status === 'active') return 'plan-active';
  const p = proposePlan(state, { sourceKey, now });
  return p.reason === 'template-missing' ? null : (p.reason ?? null);
}

/** Signaler där en omplanering kan hjälpa. Källpostens egna ändringar granskas först i posten. */
export function isReplannable(s: PlanSignal): boolean {
  return s.kind === 'deadline-moved' || s.kind === 'zone-changed' || s.kind === 'conflict' || s.kind === 'after-deadline';
}

/** Antal kommande pass som "Ta bort kommande pass" tar bort – exakt samma regel som godkännandet (core). */
export function futureSessionCount(state: AppState, plan: PrepPlan, now: Temporal.Instant): number {
  try {
    return buildPlanCommit(state, { kind: 'end', opId: 'preview', planId: plan.id, sourceKey: plan.sourceKey, expectedPlanRevision: plan.revision, removeFuture: true }, now).events.length;
  } catch {
    return 0;
  }
}

/**
 * Planens framsteg: "1 av 2 klara". "Alla förberedelser klara" gäller bara passen – deadlinen klarmarkeras
 * aldrig av planen, och inget antyder att något lämnats in. Synkar = något pass saknas på enheten (aldrig "komplett").
 */
export function planProgress(review: PlanReview): { text: string; note: string | null; tone: PrepTone; icon: PrepIcon } {
  if (review.completeness === 'syncing') {
    return { text: tr('Synkar', 'Syncing'), note: tr('Alla pass i planen finns inte på den här enheten än.', 'Not all sessions in the plan are on this device yet.'), tone: 'warn', icon: 'syncing' };
  }
  if (review.allDone) {
    return {
      text: tr('Alla förberedelser klara', 'All preparation sessions done'),
      note: tr('Själva deadlinen är inte markerad klar – det gör du själv när du kontrollerat att åtgärden är utförd.', 'The deadline itself is not marked done – you do that yourself once you have checked that the action is done.'),
      tone: 'ok',
      icon: 'done',
    };
  }
  return { text: tr(`${review.doneCount} av ${review.activeCount} klara`, `${review.doneCount} of ${review.activeCount} done`), note: null, tone: review.doneCount ? 'ok' : 'muted', icon: review.doneCount ? 'done' : 'planned' };
}

/** Ett mallsteg: "Samla underlag · 45 min · helst 3 vardagar före". Titeln är användarens egen text. */
export function stepLine(step: Pick<PrepStepDef, 'title' | 'durationMinutes' | 'daysBefore'>, dayCount: PrepTemplateContent['dayCount']): string {
  return `${step.title} · ${formatMinutes(step.durationMinutes)} · ${tr('helst', 'preferably')} ${stepDaysText(step.daysBefore, dayCount)}`;
}

/** Regelns läge för en importkälla: "Av", "På · mall: X", "Pausad: mallen togs bort". Mallnamnet är användarens eget. */
export function ruleStatusText(rule: Pick<PrepRule, 'status' | 'pausedReason'> | undefined, templateName: string | null): string {
  if (!rule) return tr('Av', 'Off');
  const tpl = templateName ? ` · ${tr('mall', 'template')}: ${templateName}` : '';
  return rule.status === 'active' ? tr('På', 'On') + tpl : pausedReasonText(rule.pausedReason) + (rule.pausedReason === 'template-deleted' ? '' : tpl);
}

/** Besked när planen avslutats. */
export function endedText(removed: number): string {
  if (!removed) return tr('Planen är avslutad. Passen ligger kvar i kalendern.', 'The plan has ended. The sessions stay in the calendar.');
  return removed === 1 ? tr('Planen är avslutad och 1 kommande pass togs bort.', 'The plan has ended and 1 upcoming session was removed.') : tr(`Planen är avslutad och ${removed} kommande pass togs bort.`, `The plan has ended and ${removed} upcoming sessions were removed.`);
}

export interface PlanSessionInfo {
  planId: string;
  sourceKey: string;
  /** 1-baserat stegnummer och antal steg i planen. */
  n: number;
  total: number;
  /** Användaren har flyttat eller ändrat passet själv – planen flyttar det inte utan uttryckligt val. */
  manual: boolean;
}

/** Är aktiviteten ett pass i en AKTIV förberedelseplan? Bara planens egna pass (aldrig en kopia). */
export function planSessionInfo(state: AppState, eventId: string): PlanSessionInfo | null {
  const owner = planEventIndex(state).get(eventId);
  if (!owner?.active) return null;
  const plan = state.prepPlans?.[owner.planId];
  const i = plan?.steps.findIndex((s) => s.eventId === eventId) ?? -1;
  if (!plan || i < 0) return null;
  const ev = state.events[eventId];
  return { planId: plan.id, sourceKey: plan.sourceKey, n: i + 1, total: plan.steps.length, manual: !!ev && ev.revision !== plan.steps[i]!.approved.eventRevision };
}

// ---------------------------------------------------------------------------
// Mallredigering: utkast och kontroll
// ---------------------------------------------------------------------------

export interface TemplateStepDraft {
  /** Radens nyckel i listan (stabil även för nya steg utan ID). */
  key: string;
  /** Befintligt stegs ID, '' för nya steg (core ger dem nya ID:n vid sparandet). */
  id: string;
  title: string;
  durationMinutes: number;
  daysBefore: number;
}
export interface TemplateDraft extends Omit<PrepTemplateContent, 'steps'> {
  steps: TemplateStepDraft[];
}

/** Ny mall: ett tomt steg, vardagar 08–17 i en egen kalender. */
export function emptyTemplateDraft(state: Pick<AppState, 'calendars'>, key = 's1'): TemplateDraft {
  return { name: '', steps: [{ key, id: '', title: '', durationMinutes: 45, daysBefore: 2 }], dayCount: 'weekdays', window: { start: '08:00', end: '17:00' }, weekdays: [1, 2, 3, 4, 5], calendarId: starterCalendarId(state) };
}

/** Befintlig mall som utkast. Stegens ID:n följer med så att stegens identitet aldrig ändras. */
export function templateDraftOf(t: PrepTemplateContent): TemplateDraft {
  return { name: t.name, steps: t.steps.map((s) => ({ key: s.id, id: s.id, title: s.title, durationMinutes: s.durationMinutes, daysBefore: s.daysBefore })), dayCount: t.dayCount, window: { ...t.window }, weekdays: [...t.weekdays], calendarId: t.calendarId };
}

/** Utkastet som mallinnehåll att spara: befintliga steg behåller sina ID:n, nya har id ''. Texterna trimmas av core. */
export function draftContent(d: TemplateDraft): PrepTemplateContent {
  return { name: d.name, steps: d.steps.map((s) => ({ id: s.id, title: s.title, durationMinutes: s.durationMinutes, daysBefore: s.daysBefore })), dayCount: d.dayCount, window: { ...d.window }, weekdays: [...d.weekdays], calendarId: d.calendarId };
}

/** Första felet i utkastet – samma regler som core (validatePrepTemplateContent); nya steg prövas med tillfälliga ID:n. */
export function draftIssue(state: Pick<AppState, 'calendars'>, d: TemplateDraft): TemplateIssue | null {
  const c = draftContent(d);
  return validatePrepTemplateContent(state, { ...c, steps: c.steps.map((s, i) => ({ ...s, id: s.id || `new:${d.steps[i]!.key}` })) });
}

/** Flyttar element i ett steg upp (-1) eller ned (+1). Utanför listan: oförändrad kopia. */
export function moveItem<T>(list: readonly T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  const out = [...list];
  if (i < 0 || j < 0 || i >= out.length || j >= out.length) return out;
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
}

/**
 * Flyttar ett mallsteg upp eller ned. "Dagar före" hör till platsen i ordningen (stegen står i tidsordning),
 * så bara steget – titel, längd och identitet – byter plats och mallen förblir giltig.
 */
export function moveStep(steps: readonly TemplateStepDraft[], i: number, dir: -1 | 1): TemplateStepDraft[] {
  return moveItem(steps, i, dir).map((s, k) => ({ ...s, daysBefore: steps[k]!.daysBefore }));
}

// ---------------------------------------------------------------------------
// Viktigt och Idag: rader och sammanfattningar
// ---------------------------------------------------------------------------

/** En länkad förberedelse: "tis 3 juni 09.00" (passets egen väggtid, som i Viktigt sedan tidigare). */
export function prepLinkWhen(p: Pick<PrepLink, 'date' | 'timing'>): string {
  return `${formatWeekdayDate(p.date)}${p.timing.kind === 'timed' ? ' ' + formatLocalClock(p.timing.start) : ''}`;
}

/** Nästa handling för en rad i Viktigt. */
export type DeadlineRowAction = 'review' | 'confirm' | 'plan' | 'find' | 'plan-review' | null;

/**
 * Rad i Viktigt: kort status för förberedelserna och EN nästa handling. Granskning och kontroll går före;
 * en aktiv plan visas som plan ("Se över" när den behöver det); annars "Planera förberedelser" när en plan
 * kan föreslås – även när enstaka förberedelser redan finns (de adopteras aldrig tyst, se förhandsvisningen).
 */
export function deadlineRowInfo(
  e: Pick<DeadlineEntry, 'next' | 'group' | 'origin' | 'recurringSource' | 'prep'>,
  ctx: { review: PlanReview | null; plannable: boolean; draft: boolean; tz: string },
): { text: string; action: DeadlineRowAction } {
  const first: DeadlineRowAction = e.next === 'review' ? 'review' : e.next === 'confirm' ? 'confirm' : null;
  if (e.origin === 'personal') return { text: tr('Egen aktivitet som du markerat som viktig.', 'Your own activity, marked as important.'), action: first };
  if (e.recurringSource) return { text: tr('Återkommande källdatum – planera förberedelser manuellt i posten.', 'Recurring source date – plan preparation manually in the item.'), action: first };
  const r = ctx.review;
  if (r && r.status === 'active') {
    const text = r.needsReview ? reviewLine(r) : planLine(r, ctx.tz);
    return { text, action: first ?? (r.needsReview && e.group !== 'done' ? 'plan-review' : null) };
  }
  const pending = e.prep.filter((p) => !p.done);
  const text = pending.length
    ? tr(`Förberedelse planerad: ${pending.map(prepLinkWhen).join(', ')}`, `Preparation planned: ${pending.map(prepLinkWhen).join(', ')}`)
    : e.prep.length
      ? tr('Förberedelsen är klarmarkerad.', 'The preparation is marked done.')
      : e.group === 'done'
        ? tr('Klar här (lokal kvittering).', 'Done here (local check-off).')
        : ctx.draft
          ? tr('Ett förslag på förberedelseplan väntar på granskning.', 'A suggested preparation plan is waiting for review.')
          : tr('Ingen förberedelse planerad.', 'No preparation planned.');
  if (first || e.group === 'done') return { text, action: first };
  return { text, action: ctx.plannable ? 'plan' : e.next === 'prepare' ? 'find' : null };
}

/** Ett automatiskt utkast i Viktigt: deadline, datum, mall och tiderna i korthet. */
export function draftRowInfo(d: AutoDraft, state: Pick<AppState, 'prepTemplates' | 'settings'>): { title: string; date: string | null; template: string | null; times: string; fits: boolean } {
  const p = d.proposal;
  return {
    title: p.sourceTitle,
    date: p.basis ? deadlineWhen(p.basis, state.settings.timeZone) : null,
    template: p.snapshot?.name ?? state.prepTemplates?.[d.templateId]?.name ?? null,
    times: draftTimesSummary(p),
    fits: p.status === 'ok',
  };
}

/** "Månadsrapport – 19 juni: har redan en förberedelseplan" (Varför saknas förslag?). Titeln är källans egen. */
export function skippedLine(s: Pick<AutoDraftSkip, 'title' | 'date' | 'reason'>): string {
  return `${s.title} – ${formatDayMonth(s.date)}: ${skipReasonText(s.reason)}`;
}

/** Idag: "2 förslag på förberedelser väntar på granskning · 1 plan behöver ses över". null = inget väntar. */
export function attentionSummary(drafts: number, reviews: number): string | null {
  const parts: string[] = [];
  if (drafts) parts.push(drafts === 1 ? tr('1 förslag på förberedelser väntar på granskning', '1 preparation suggestion is waiting for review') : tr(`${drafts} förslag på förberedelser väntar på granskning`, `${drafts} preparation suggestions are waiting for review`));
  if (reviews) parts.push(reviews === 1 ? tr('1 plan behöver ses över', '1 plan needs a look') : tr(`${reviews} planer behöver ses över`, `${reviews} plans need a look`));
  return parts.length ? parts.join(' · ') : null;
}

/** Nytt steg sist: önskad dag närmare deadline än föregående steg (stegen står i tidsordning). */
export function nextStepDraft(steps: readonly TemplateStepDraft[], key: string): TemplateStepDraft {
  const prev = steps.at(-1);
  return { key, id: '', title: '', durationMinutes: 45, daysBefore: prev ? Math.max(PREP_MIN_DAYS_BEFORE, prev.daysBefore - 1) : 1 };
}
